import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { TableDynamicColumn } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  createEmptyObjectPassportElement,
  mergeLoadedObjectPassportElement,
  type ObjectPassportElement,
} from '@/features/Inventory/ObjectListEdit/model/model'
import { createRuntimeFieldValueMap, type RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { mergeFieldMetadata } from './mergeFieldMetadata'

/** Filter passed through to linked / dictionary resolution for table cells. */
export type PassportRuntimeListTableFilter = { status_date: string | null }

/** Minimal HTTP client used by column display resolution (matches `useHttp`). */
export type PassportRuntimeListTableHttp = {
  get: (url: string) => Promise<{ data?: unknown }>
}

/** Same contract as `resolveRuntimeTableColumnDisplay` (injected to avoid a hard dependency for tests / tree-shaking). */
export type ResolveRuntimeTableColumnDisplay = (
  http: PassportRuntimeListTableHttp,
  filter: PassportRuntimeListTableFilter,
  groupId: string,
  childCatalogTypeId: number,
  childFieldValues: RuntimeFieldValueMap,
  column: TableDynamicColumn,
  catalogFields: ObjectInventoryField[],
  fieldResolved: ObjectInventoryField | undefined,
) => Promise<string>

export type PassportRuntimeListTableRow = {
  childElementId: number
  name: string
  columnCells: Record<string, string>
  /** Geometry for map affordance; omit/undefined when unknown (batch rows) — map stays disabled per spec. */
  coordinates: unknown
}

export { mergeFieldMetadata } from './mergeFieldMetadata'

export function fieldMapForElement(
  element: ObjectPassportElement,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): RuntimeFieldValueMap {
  const raw = element.fields
  const arr = Array.isArray(raw)
    ? raw
    : Object.entries(raw ?? {}).map(([id, value]) => ({ id, value }))
  return createRuntimeFieldValueMap(fieldMetadata ?? element.invent_object_type?.fields, arr)
}

/**
 * Maps bulk `GET table-bound-passports` `data[]` elements (same shape as `GET /passport`) to
 * table rows, reusing the legacy per-child display path but **without** `fetchPassportChild`.
 * Row order matches the input array (server binding order).
 *
 * @param resolveColumnDisplay — pass `resolveRuntimeTableColumnDisplay` from `resolveLinkedPassportValues` at the call site.
 */
export async function buildRowsFromPassportElements(
  http: PassportRuntimeListTableHttp,
  filter: PassportRuntimeListTableFilter,
  objectId: string,
  elements: ReadonlyArray<Partial<ObjectPassportElement>>,
  cols: TableDynamicColumn[],
  allFields: ObjectInventoryField[],
  /** Catalog type id for rows of this LIST (`layoutItem.inventory_type`); skip others. */
  expectedChildCatalogTypeId: number,
  resolveColumnDisplay: ResolveRuntimeTableColumnDisplay,
): Promise<PassportRuntimeListTableRow[]> {
  const rows: PassportRuntimeListTableRow[] = []
  for (const raw of elements) {
    const child = mergeLoadedObjectPassportElement(createEmptyObjectPassportElement(), raw)
    if (!child?.id) continue
    const childTypeId = Number(child.invent_object_type?.id) || 0
    if (
      expectedChildCatalogTypeId > 0 &&
      childTypeId > 0 &&
      childTypeId !== expectedChildCatalogTypeId
    ) {
      continue
    }
    const catalogFields = mergeFieldMetadata(
      child.invent_object_type?.fields as ObjectInventoryField[],
      allFields,
    )
    const metaById = new Map(catalogFields.map((f) => [String(f.id), f]))
    const childMap = fieldMapForElement(child, catalogFields)
    const columnCells: Record<string, string> = {}
    for (const col of cols) {
      const fieldResolved = metaById.get(String(col.field_id))
      columnCells[String(col.id)] = await resolveColumnDisplay(
        http,
        filter,
        objectId,
        childTypeId,
        childMap,
        col as TableDynamicColumn,
        catalogFields,
        fieldResolved,
      )
    }
    const rawCoords = (child as Record<string, unknown>).coordinates
    rows.push({
      childElementId: Number(child.id),
      name: String(child.name ?? '').trim() || '—',
      columnCells,
      coordinates: rawCoords,
    })
  }
  return rows
}
